import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import { ensureArtistQr, getArtistQr } from '@/services/artistQr.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';
import Text from '@/shared-components/text/components/Text';

import {
  buildArtistQrFilename,
  downloadQrPng,
  normalizeArtistQrPayload,
} from './utils';

import './index.css';

export default function ArtistQrSettings() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.settings_menu.preferences.artist_qr');
  const [qrData, setQrData] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const session = state.user_session;
  const hasQr = Boolean(qrData?.qrUrl);
  const displayUrl = useMemo(() => qrData?.qrUrl || '', [qrData]);

  useEffect(() => {
    let ignore = false;
    const existingQr = normalizeArtistQrPayload(session);

    if (existingQr) {
      setQrData(existingQr);
      setCheckingExisting(false);
      return () => {
        ignore = true;
      };
    }

    setCheckingExisting(true);
    setError('');
    getArtistQr()
      .then((loadedQr) => {
        if (ignore) {
          return;
        }
        setQrData(loadedQr);
        if (loadedQr?.token) {
          stateHandler.set('user_session.artist_qr', {
            token: loadedQr.token,
            created_at: loadedQr.createdAt || loadedQr.created_at || null,
            created_by_artist_id: loadedQr.createdByArtistId || loadedQr.created_by_artist_id || null,
          });
        }
      })
      .catch((loadError) => {
        if (ignore) {
          return;
        }
        setError(loadError?.message || t('errors.load'));
        setQrData(null);
      })
      .finally(() => {
        if (!ignore) {
          setCheckingExisting(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [session, stateHandler, t]);

  const qrSvgElement = () => qrRef.current?.querySelector('svg') ?? null;

  async function handleGenerate() {
    if (loading) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      const nextQrData = await ensureArtistQr();
      setQrData(nextQrData);
      if (nextQrData?.token) {
        stateHandler.set('user_session.artist_qr', {
          token: nextQrData.token,
          created_at: nextQrData.createdAt || nextQrData.created_at || null,
          created_by_artist_id: nextQrData.createdByArtistId || nextQrData.created_by_artist_id || null,
        });
      }
    } catch (generateError) {
      setError(generateError?.message || t('errors.generate'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPng() {
    const svg = qrSvgElement();
    if (!svg || downloading) {
      return;
    }

    setDownloading(true);
    setError('');
    try {
      await downloadQrPng(svg, buildArtistQrFilename(session, 'png'));
    } catch (downloadError) {
      setError(downloadError?.message || t('errors.download'));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <SettingsHeading text={t('heading')} />

      <div className="settings-artist-qr__container">
        {checkingExisting ? (
          <div className="settings-artist-qr__state">
            <ContinueButton
              className="settings-artist-qr__button"
              loading
              turnOff
            >
              {t('checking')}
            </ContinueButton>
          </div>
        ) : !hasQr ? (
          <div className="settings-artist-qr__state">
            <Text className="settings-artist-qr__copy font-size-small" paragraph>
              {t('intro')}
            </Text>
            <ContinueButton
              className="settings-artist-qr__button"
              loading={loading}
              onClick={handleGenerate}
            >
              {t('generate')}
            </ContinueButton>
          </div>
        ) : (
          <div className="settings-artist-qr__preview">
            <div className="settings-artist-qr__qr-frame" ref={qrRef}>
              <QRCode
                className="settings-artist-qr__qr"
                value={displayUrl}
                size={240}
                level="M"
              />
            </div>

            <div className="settings-artist-qr__meta">
              {qrData?.isActive === false && (
                <Text className="settings-artist-qr__warning font-size-small" paragraph>
                  {t('inactive_warning')}
                </Text>
              )}
              <Text className="settings-artist-qr__url font-size-small" paragraph>
                {displayUrl}
              </Text>
            </div>

            <ContinueButton
              className="settings-artist-qr__button"
              loading={downloading}
              onClick={handleDownloadPng}
            >
              {t('download_png')}
            </ContinueButton>
          </div>
        )}

        {error && (
          <Text className="settings-artist-qr__error font-size-small" paragraph>
            {error}
          </Text>
        )}
      </div>
    </>
  );
}
